import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Send, Sparkles, AlertTriangle } from "lucide-react";

const STARTERS = [
  "I feel anxious",
  "I feel unsafe",
  "I want to self harm",
  "I feel alone",
];

export default function Assistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hello, I'm here to help you follow your safety plan. If you are in immediate danger please call 999 or NHS 111 now. Tell me what's happening in your own words." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "", streaming: true }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("sh_token");
      const language = localStorage.getItem("sh_lng") || "en";
      const res = await fetch(`${API_BASE}/assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text, session_id: sessionId, language }),
      });

      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const evt of events) {
          const lines = evt.split("\n");
          let isDone = false;
          let dataParts = [];
          for (const ln of lines) {
            if (ln.startsWith("event: done")) isDone = true;
            if (ln.startsWith("data: ")) dataParts.push(ln.slice(6));
          }
          const data = dataParts.join("\n");
          if (isDone) {
            if (data) setSessionId(data);
            continue;
          }
          if (data) {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, text: (last.text || "") + data };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "I'm having trouble connecting. Please call 999 or NHS 111 if you need urgent help." };
        return copy;
      });
    } finally {
      setStreaming(false);
      setMessages((m) => m.map((msg, i) => (i === m.length - 1 ? { ...msg, streaming: false } : msg)));
    }
  };

  return (
    <div className="space-y-4 fade-up" data-testid="assistant-page">
      <header>
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-teal-700" />
          <h1 className="font-display text-3xl font-bold text-slate-900">AI Safety-Plan Companion</h1>
        </div>
        <p className="text-slate-600 mt-2 max-w-xl">Powered by Claude Sonnet 4.6. I'll help you follow your own plan — I don't replace clinicians or emergency services.</p>
      </header>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="assistant-disclaimer">
        <AlertTriangle className="w-4 h-4 mt-0.5" />
        This assistant supports your safety plan. It does not provide clinical advice or assess risk. In emergencies, call 999 or NHS 111.
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col" style={{ height: "60vh" }} data-testid="assistant-chat">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-teal-700 text-white ml-auto"
                : "bg-slate-100 text-slate-900"
            }`}
            data-testid={`msg-${m.role}-${i}`}
            >
              {m.text}
              {m.streaming && !m.text && <span className="opacity-60">Thinking…</span>}
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 p-3">
          <div className="flex flex-wrap gap-2 mb-2">
            {STARTERS.map((s) => (
              <button
                key={s}
                data-testid={`starter-${s.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => send(s)}
                disabled={streaming}
                className="text-xs border border-slate-300 hover:border-teal-500 hover:bg-teal-50 rounded-full px-3 py-1.5 text-slate-700"
              >
                {s}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="flex gap-2"
          >
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Tell me what's happening…"
              className="flex-1 border border-slate-300 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
            <button
              data-testid="assistant-send"
              type="submit"
              disabled={streaming || !input.trim()}
              className="bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white rounded-xl px-4 py-2.5 flex items-center gap-1"
            >
              <Send className="w-4 h-4" /> Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
